import { candles, distributions } from '~data';

// One unit of a fund, valued over time, on one of three bases.
//
// The three answer different questions and a covered-call ETF can look like a
// disaster on the first and a success on the third, which is the entire reason
// to be able to switch:
//
//   nav    the unit price alone. What the fund is worth. On a fund paying most
//          of its distribution out of capital this only ever falls.
//   cash   price plus every distribution, banked as it arrives and never
//          reinvested. What a holder living off the income actually has.
//   drip   distributions buy more units at the close they are paid on, so the
//          position compounds. What a holder accumulating actually has.
//
// All three are per single unit held from the start of the window, so they are
// directly comparable and can be indexed to a common base without any of them
// having been scaled differently from the others.

export type Basis = 'nav' | 'cash' | 'drip';

export const BASIS_LABEL: Record<Basis, string> = {
    nav: 'NAV',
    cash: 'NAV + cash',
    drip: 'NAV + DRIP',
};

export const BASIS_NOTE: Record<Basis, string> = {
    nav: 'unit price alone',
    cash: 'distributions banked as paid, never reinvested',
    drip: 'distributions buy units at the close they are paid on',
};

export interface SeriesPoint {
    t: number;
    value: number;
}

export const valueSeries = (symbol: string, basis: Basis, from: number): SeriesPoint[] => {
    const bars = candles(symbol).filter((c) => Date.parse(c.date) >= from);
    if (bars.length === 0) return [];

    if (basis === 'nav') return bars.map((c) => ({ t: Date.parse(c.date), value: c.close }));

    // Banked on the pay date — when the cash is actually in hand — falling back
    // to the ex-date for a fund that publishes no pay date.
    const paid = distributions(symbol)
        .map((d) => ({ t: Date.parse(d.payDate ?? d.exDate), amount: d.amount }))
        .filter((d) => d.t >= from)
        .sort((a, b) => a.t - b.t);

    let next = 0;
    let cash = 0;
    let units = 1;

    return bars.map((c) => {
        const t = Date.parse(c.date);

        // Every distribution that has landed by this bar. A loop rather than a
        // filter because the payments are walked once across the whole series,
        // not rescanned per bar.
        while (next < paid.length && paid[next].t <= t) {
            const payout = units * paid[next].amount;
            if (basis === 'cash') cash += payout;
            // Fractional units are allowed: this measures the strategy, not a
            // broker that would round you down and leave the remainder idle.
            else if (c.close > 0) units += payout / c.close;
            next += 1;
        }

        return { t, value: basis === 'cash' ? c.close + cash : units * c.close };
    });
};
